import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { FolderOpen, Building2, Briefcase, Rocket, ChevronRight, ChevronLeft, Sparkles, SkipForward, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OnboardingPage = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const [companyName, setCompanyName] = useState('');
  const [role, setRole] = useState(user?.role || 'Owner');
  const [projectName, setProjectName] = useState('');
  const [clientName, setClientName] = useState('');
  const [budget, setBudget] = useState('');

  const handleUpdateCompany = async () => {
    if (!companyName.trim() && !role) return;
    setLoading(true);
    try {
      await axios.put(`${API}/onboarding/update-company`, {
        company_name: companyName.trim() || undefined,
        role
      }, { withCredentials: true });
    } catch (err) {
      console.error('update-company failed:', err);
    }
    setLoading(false);
    setStep(1);
  };

  const handleCreateProject = async () => {
    if (!projectName.trim()) {
      toast.error('Enter a project name');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/onboarding/create-first-project`, {
        name: projectName.trim(),
        client_name: clientName.trim(),
        budget: parseFloat(budget) || 0
      }, { withCredentials: true });
      setStep(2);
    } catch {
      toast.error('Failed to create project');
    }
    setLoading(false);
  };

  const handleSeedDemo = async () => {
    setDemoLoading(true);
    try {
      await axios.post(`${API}/onboarding/seed-demo`, {}, { withCredentials: true });
      toast.success('Demo data loaded!');
      setStep(2);
    } catch {
      toast.error('Failed to load demo data');
    }
    setDemoLoading(false);
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/onboarding/complete`, {}, { withCredentials: true });
      await checkAuth();
      navigate('/dashboard');
    } catch {
      navigate('/dashboard');
    }
    setLoading(false);
  };

  const handleSkip = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/onboarding/complete`, {}, { withCredentials: true });
      await checkAuth();
    } catch (err) {
      console.error('onboarding complete failed:', err);
    }
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex flex-col" data-testid="onboarding-page">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-primary p-1.5">
            <FolderOpen className="w-4 h-4 text-black" />
          </div>
          <span className="font-heading text-base font-bold uppercase tracking-wider text-zinc-900">Punchlist</span>
        </div>
        <button
          onClick={handleSkip}
          data-testid="onboarding-skip-btn"
          className="text-xs font-heading font-semibold uppercase tracking-wider text-zinc-400 hover:text-zinc-700 flex items-center gap-1 transition-colors"
        >
          Skip <SkipForward className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        {/* Progress Dots */}
        <div className="flex items-center gap-3 mb-10" data-testid="onboarding-progress">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className={`h-1.5 transition-all duration-300 ${
                i < step ? 'w-8 bg-primary' :
                i === step ? 'w-8 bg-primary' :
                'w-8 bg-zinc-200'
              }`} data-testid={`progress-dot-${i}`} />
            </div>
          ))}
        </div>

        <div className="w-full max-w-md">
          {/* Step 0: Company & Role */}
          {step === 0 && (
            <div className="bg-white border border-zinc-200 p-6 sm:p-8" data-testid="onboarding-step-0">
              <h2 className="font-heading text-2xl font-bold uppercase tracking-wide text-zinc-900 mb-1">Welcome to Punchlist</h2>
              <p className="text-sm text-zinc-500 mb-6">Let's set up your first project.</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2">Company Name</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={e => setCompanyName(e.target.value)}
                    data-testid="onboarding-company-name"
                    className="w-full h-12 border border-zinc-200 bg-white text-zinc-900 text-sm px-4 focus:outline-none focus:border-primary transition-colors"
                    placeholder="Your company name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2">Your Role</label>
                  <select
                    value={role}
                    onChange={e => setRole(e.target.value)}
                    data-testid="onboarding-role-select"
                    className="w-full h-12 border border-zinc-200 bg-white text-zinc-900 text-sm px-4 focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="Owner">Owner</option>
                    <option value="Project Manager">Project Manager</option>
                    <option value="Foreman">Foreman</option>
                  </select>
                </div>
              </div>

              <button
                onClick={handleUpdateCompany}
                disabled={loading}
                data-testid="onboarding-next-0"
                className="w-full mt-6 h-12 bg-primary text-black font-heading font-bold uppercase tracking-wider text-sm hover:bg-primary-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Step 1: Create Project or Demo */}
          {step === 1 && (
            <div className="bg-white border border-zinc-200 p-6 sm:p-8" data-testid="onboarding-step-1">
              <h2 className="font-heading text-2xl font-bold uppercase tracking-wide text-zinc-900 mb-1">Create Your First Project</h2>
              <p className="text-sm text-zinc-500 mb-6">Or load demo data to explore right away.</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2">Project Name</label>
                  <input
                    type="text"
                    value={projectName}
                    onChange={e => setProjectName(e.target.value)}
                    data-testid="onboarding-project-name"
                    className="w-full h-12 border border-zinc-200 bg-white text-zinc-900 text-sm px-4 focus:outline-none focus:border-primary transition-colors"
                    placeholder="e.g. Main Street Office Build"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2">Client</label>
                  <input
                    type="text"
                    value={clientName}
                    onChange={e => setClientName(e.target.value)}
                    data-testid="onboarding-client-name"
                    className="w-full h-12 border border-zinc-200 bg-white text-zinc-900 text-sm px-4 focus:outline-none focus:border-primary transition-colors"
                    placeholder="Client name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2">Budget</label>
                  <input
                    type="number"
                    value={budget}
                    onChange={e => setBudget(e.target.value)}
                    data-testid="onboarding-budget"
                    className="w-full h-12 border border-zinc-200 bg-white text-zinc-900 text-sm px-4 font-mono focus:outline-none focus:border-primary transition-colors"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => setStep(0)}
                  className="h-12 px-4 border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 transition-colors flex items-center"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={handleCreateProject}
                  disabled={loading}
                  data-testid="onboarding-create-project-btn"
                  className="flex-1 h-12 bg-primary text-black font-heading font-bold uppercase tracking-wider text-sm hover:bg-primary-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Project'}
                </button>
              </div>

              <div className="flex items-center my-6">
                <div className="flex-1 h-px bg-zinc-200" />
                <span className="px-3 text-[10px] font-heading font-semibold text-zinc-400 uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-zinc-200" />
              </div>

              <button
                onClick={handleSeedDemo}
                disabled={demoLoading}
                data-testid="onboarding-demo-data-btn"
                className="w-full h-12 bg-zinc-900 text-white font-heading font-bold uppercase tracking-wider text-sm hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Sparkles className="w-4 h-4" />
                {demoLoading ? 'Loading Demo Data...' : 'Try with Demo Data'}
              </button>
              <p className="text-[10px] text-zinc-400 text-center mt-2 font-mono tracking-wide">
                3 PROJECTS / 10 PHASES / 20 ORDERS
              </p>
            </div>
          )}

          {/* Step 2: Done */}
          {step === 2 && (
            <div className="bg-white border border-zinc-200 p-6 sm:p-8 text-center" data-testid="onboarding-step-2">
              <div className="w-14 h-14 bg-primary flex items-center justify-center mx-auto mb-6">
                <Rocket className="w-7 h-7 text-black" />
              </div>
              <h2 className="font-heading text-2xl font-bold uppercase tracking-wide text-zinc-900 mb-1">You're all set!</h2>
              <p className="text-sm text-zinc-500 mb-8">Your dashboard is ready. Start tracking materials and budgets.</p>

              <button
                onClick={handleFinish}
                disabled={loading}
                data-testid="onboarding-finish-btn"
                className="w-full h-12 bg-primary text-black font-heading font-bold uppercase tracking-wider text-sm hover:bg-primary-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                Go to Dashboard <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
